import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges, OnDestroy, Output, EventEmitter, AfterViewInit
} from '@angular/core';
import * as c3 from 'c3';
import { GraphUtilsService } from '../../services/graph-utils.service';
import { DataService } from '../../services/data.service';
let vm: any;
@Component({
  selector: 'app-c3-barchart-dashboard',
  templateUrl: './c3-barchart-dashboard.component.html',
  styleUrls: ['./c3-barchart-dashboard.component.scss'],
  standalone: false
})
export class C3BarchartDashboardComponent implements OnChanges, AfterViewInit, OnDestroy {
  private readonly maxUnrotatedXAxisLabels = 7;
  private readonly maxXAxisLabelLength = 20;

  @Input() drillData: any;
  @Input() colour: any;
  @Input() from: string = '';
  @Input() matGrpValue = '';
  @Input() currency = '';
  @Output() selectedItem: EventEmitter<object> = new EventEmitter();

  egaChart: any = null;
  subscription: any;
  selectedPoints: any[] = [];
  colorObj = {};
  constructor(
    private elRef: ElementRef,
    private graphUtil: GraphUtilsService,
    private dataSer: DataService
  ) {
    this.subscription = dataSer.events.subscribe(this.replotGraphEventSubscription);
    vm = this;
  }

  replotGraphEventSubscription = (res: any) => {
    if (res.name === 'replotGraph') {
      let timeoutval = 0;
      if (res.args && res.args[0] === 'menuToggle') {
        timeoutval = 101;
      }
      setTimeout(() => {
        this.checkAndReplotGraph();
      }, timeoutval);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    //  changed to fix memory leak
    if (this.egaChart) {
      this.egaChart.destroy();
    }
    this.checkAndReplotGraph();
  }

  ngAfterViewInit(): void {
    this.checkAndReplotGraph();
  }

  checkAndReplotGraph() {
    this.generateChart(this.drillData);
    this.selectedPoints = [];
  }

  private toNumber(value: any): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private shouldShowSecondaryAxis(): boolean {
    return this.matGrpValue === 'cost' || this.matGrpValue === 'ton';
  }

  private getCurrencySuffix(): string {
    const currency = String(this.currency || '').trim();
    return currency ? ` (${currency})` : '';
  }

  private getPrimaryLabel(): string {
    if (this.matGrpValue === 'cost') {
      return 'Total Cost';
    }
    if (this.matGrpValue === 'ton') {
      return 'Total Ton';
    }
    return 'Percentage';
  }

  private getPrimaryAxisLabel(): string {
    if (this.matGrpValue === 'cost') {
      return `Give Away Cost${this.getCurrencySuffix()}`;
    }
    if (this.matGrpValue === 'ton') {
      return 'Give Away Tons';
    }
    return '';
  }

  private getPrimaryLegendLabel(): string {
    if (this.matGrpValue === 'cost') {
      return `Give Away Cost${this.getCurrencySuffix()}`;
    }
    if (this.matGrpValue === 'ton') {
      return 'Give Away Tons';
    }
    return 'Give Away %';
  }

  private shouldRotateXAxisLabels(): boolean {
    return (this.drillData?.length || 0) > this.maxUnrotatedXAxisLabels;
  }

  private formatXAxisLabel(label: any): string {
    const value = String(label || '');
    if (value.length <= this.maxXAxisLabelLength) {
      return value;
    }

    return `${value.slice(0, this.maxXAxisLabelLength)}..`;
  }

  generateChart = (data: any) => {
    if (this.matGrpValue === 'cost') {
      this.colorObj = { cost: this.colour };
    } else if (this.matGrpValue === 'ton') {
      this.colorObj = { ton: this.colour };
    } else {
      this.colorObj = { percentage: this.colour };
    }
    const showSecondaryAxis = this.shouldShowSecondaryAxis();
    const graph: any = {
      bindto: this.elRef.nativeElement.children[0],
      data: {
        columns: [
          ['primary', ...this.drillData.map((elem: any) => this.toNumber(elem.value))],
          ...(showSecondaryAxis
            ? [['percentage', ...this.drillData.map((elem: any) => this.toNumber(elem.percentage))]]
            : [])
        ],
        type: 'bar',
        types: {
          primary: 'bar',
          percentage: 'line'
        },
        axes: {
          primary: 'y',
          percentage: 'y2'
        },
        names: {
          primary: this.getPrimaryLegendLabel(),
          percentage: 'Give Away %'
        },
        colors: {
          primary: this.colour,
          percentage: '#205f83'
        },
        onclick: (d: any) => {
          this.onBarClick(d);
        }
      },
      bar: {
        width: 30
      },
      axis: {
        rotated: false,
        x: {
          type: 'category',
          height: 60,
          tick: {
            multiline: false,
            rotate: this.shouldRotateXAxisLabels() ? -20 : 0,
            outer: false
          },
          categories: this.drillData.map((elem: any) => this.formatXAxisLabel(elem.name))
        },
        y: {
          label: {
            text: this.getPrimaryAxisLabel(),
            position: 'outer-middle'
          }
        },
        y2: {
          show: showSecondaryAxis,
          label: {
            text: 'Give Away %',
            position: 'outer-middle'
          },
          tick: {
            format: (value: number) => `${value}%`
          }
        }
      },
      legend: {
        show: true,
        position: 'bottom'
      },
      point: {
        show: showSecondaryAxis
      }
    };
    if (this.from === 'drill-down') {
      graph.tooltip = this.createTooltip(showSecondaryAxis, this.getPrimaryLabel());
    }
    this.egaChart = c3.generate(graph);
  }

  onBarClick(item: any) {
    if (item) {
      this.selectedItem.emit({ type: this.drillData[0].type, index: item.index });
    }
  }

  createTooltip(showSecondaryAxis: boolean, primaryLabel: string): any {
    const formatDecimal = (value: any, decimals: number): string => {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) {
        return '0';
      }
      return parsed.toFixed(decimals);
    };

    const formatCost = (value: any): string => {
      const amount = formatDecimal(value, 3);
      const currencyLabel = String(vm.currency || '').trim();
      return currencyLabel ? `${amount} ${currencyLabel}` : amount;
    };

    const formatPercentage = (value: any): string => `${formatDecimal(value, 4)}%`;

    const tooltip = {
      format: {
        value: (value: any, ratio: any, id: any) => {
          return value;
        }
      },
      contents: function (d: any[], title: any, value: any, color: any) {
        const index: number = d[0].index;
        const bar = vm.drillData[index];
        const primaryData = d.find((item: any) => item.id === 'primary');
        const percentageData = d.find((item: any) => item.id === 'percentage');
        const primaryValue = primaryData ? primaryData.value : bar.value;
        const secondaryValue = percentageData ? percentageData.value : bar.percentage;
        const formattedPrimaryValue = vm.matGrpValue === 'cost'
          ? formatCost(primaryValue)
          : vm.matGrpValue === 'percentage'
            ? formatPercentage(primaryValue)
            : formatDecimal(primaryValue, 4);
        const formattedSecondaryValue = formatPercentage(secondaryValue);
        const str = `<table class="c3-tooltip">
                      <tbody>
                        <tr>
                            <th colspan="2">${bar.name}</th>
                        </tr>
                        <tr class="c3-tooltip-name--value">
                            <td class="name"><span style="background-color:${vm.colour}"></span>${primaryLabel}</td>
                            <td class="value">${formattedPrimaryValue}</td>
                        </tr>
                        ${showSecondaryAxis ? `<tr class="c3-tooltip-name--value">
                          <td class="name"><span style="background-color:#349dd5"></span>Give Away %</td>
                            <td class="value">${formattedSecondaryValue}</td>
                        </tr>` : ''}
                      </tbody>
                  </table>`;
        return str;
      }
    };
    return tooltip;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
